import { FileArrowUpIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { FileMimeTypes, Spinner } from '@/modules/common';

import { useEventProducts, useEventStations } from '../api';
import { InventoryActions } from './inventory-actions';
import { InventoryTable } from './inventory-table';
import { Stations } from './stations';

interface InventoryProps {
  eventId: string;
  editMode?: boolean;
}

export function Inventory({ eventId, editMode = false }: InventoryProps) {
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedStationId, setSelectedStationId] = useState<string>('');

  const { stationsQuery } = useEventStations(eventId, true);

  const {
    isFetching,
    products,
    uploadEventProducts,
    getGenerateReportUrl,
    isGeneratingReport,
  } = useEventProducts(eventId, selectedStationId);

  const pickFile = () => {
    fileInputRef.current.click();
  };

  const uploadProducts = async (file: File) => {
    setIsUploading(true);
    try {
      await uploadEventProducts(file);
      await queryClient.invalidateQueries({
        queryKey: ['eventProducts', eventId, selectedStationId],
      });
      toast.success('Inventory uploaded successfully');
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const pickFileAndUpload = async (event) => {
    if (!event.target.files.length) return;

    const file = event.target.files[0];

    if (editMode) {
      await uploadProducts(file);
    }
  };

  return (
    <div>
      {isFetching || isUploading || stationsQuery.isFetching ? (
        <Spinner />
      ) : (
        <>
          <Stations
            selectedStationId={selectedStationId}
            setSelectedStationId={setSelectedStationId}
            stations={stationsQuery.data || []}
            eventId={eventId}
          />
          {selectedStationId && (
            <>
              <InventoryTable
                eventId={eventId}
                products={products}
                stationId={selectedStationId}
              />
              <div className="flex gap-2">
                <Button
                  disabled={isFetching}
                  variant="transparent"
                  type="button"
                  onClick={pickFile}
                  data-testid="upload-inventory-button"
                >
                  <FileArrowUpIcon />
                  Upload from CSV
                  <input
                    id="inventory-file"
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    data-testid="upload-inventory-file-input"
                    onChange={pickFileAndUpload}
                    accept={FileMimeTypes.Csv}
                  />
                </Button>

                {products.length > 0 && (
                  <InventoryActions
                    eventId={eventId}
                    stationId={selectedStationId}
                    onGenerateReport={getGenerateReportUrl}
                    isGeneratingReport={isGeneratingReport}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
